import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScoreBadge, RiskMeter } from "../components/StockBits";
import { InfoTip } from "../components/Glossary";
import { toast } from "sonner";
import { Search, Sparkles, TrendingUp, TrendingDown, AlertTriangle, Bot, Loader2, Clock, Columns2, Newspaper, Star, History } from "lucide-react";
import { cn } from "../lib/utils";

const EXAMPLES = ["AAPL", "TSLA", "AMZN", "GOOGL", "AMD", "SPY", "COIN"];

function Bar({ label, value, weight, tint, term }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium flex items-center gap-1">
          {label} <span className="text-xs text-muted-foreground">· {Math.round(weight * 100)}%</span>
          {term && <InfoTip term={term} />}
        </span>
        <span className="font-mono text-sm font-semibold">{value}</span>
      </div>
      <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-700", tint)} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function NewsCard({ news }) {
  if (!news) return null;
  const label = news.label || "Neutral";
  const tint = label === "Bullish" ? "text-emerald-500" : label === "Bearish" ? "text-red-500" : "text-amber-500";
  const nudge = news.nudge || 0;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="news-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Newspaper className="h-3.5 w-3.5" /> News Sentiment {news.ai_generated ? "· AI" : "· neutral fallback"}
        </span>
        <span className={cn("text-sm font-bold", tint)}>{label}</span>
      </div>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs text-muted-foreground">Score adjustment:</span>
        <span className={cn("font-mono text-sm font-bold px-2 py-0.5 rounded-md border",
          nudge > 0 ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/10"
            : nudge < 0 ? "text-red-500 border-red-500/30 bg-red-500/10"
              : "text-muted-foreground border-border")}>
          {nudge > 0 ? `+${nudge}` : nudge} pts
        </span>
        <span className="text-xs text-muted-foreground">→ news-adjusted read <strong className="text-foreground font-mono">{news.adjusted_score}</strong></span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{news.rationale}</p>
      {news.headlines?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {news.headlines.slice(0, 5).map((h, i) => (
            <li key={i} className="text-xs text-muted-foreground flex gap-2">
              <span className="text-primary">›</span>
              <span><span className="text-foreground/90">{h.title}</span>{h.publisher ? ` — ${h.publisher}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Scorecard({ data }) {
  const { user, refreshUser } = useAuth();
  const up = data.ai?.prediction_direction === "up";
  const pct = data.ai?.prediction_pct ?? 50;
  const band = data.ai?.prediction_band || [Math.max(0, pct - 8), Math.min(100, pct + 8)];
  const starred = (user?.watchlist || []).includes(data.ticker);
  const [history, setHistory] = useState([]);

  const loadHistory = () => {
    api.get(`/alerts/history/${data.ticker}`).then(({ data }) => setHistory(data.events || [])).catch(() => {});
  };
  useEffect(loadHistory, [data.ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStar = async () => {
    try {
      const { data: res } = await api.post("/watchlist/toggle", { ticker: data.ticker });
      await refreshUser();
      toast.success(res.starred ? `Pinned ${data.ticker} to your watchlist` : `Removed ${data.ticker} from watchlist`);
    } catch {
      toast.error("Could not update watchlist");
    }
  };

  return (
    <div className="space-y-4 ss-fade-up" data-testid={`scorecard-${data.ticker}`}>
      <div className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xl font-bold">{data.ticker}</span>
            {data.in_watchlist && <span className="text-[10px] font-mono uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">In watchlist</span>}
          </div>
          <p className="text-sm text-muted-foreground">{data.company} · <span className="font-mono">${data.price}</span></p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid={`star-${data.ticker}`} onClick={toggleStar} aria-label="Pin to watchlist"
            className={cn("h-10 w-10 rounded-full flex items-center justify-center border transition-colors",
              starred ? "text-amber-500 border-amber-500/40 bg-amber-500/10" : "text-muted-foreground border-border hover:text-amber-500 hover:border-amber-500/40")}>
            <Star className={cn("h-5 w-5", starred && "fill-amber-500")} />
          </button>
          <div className="text-center">
            <ScoreBadge score={data.score} size="lg" testId={`scorecard-score-${data.ticker}`} />
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">Model Score</p>
          </div>
        </div>
      </div>

      <div className={cn("rounded-2xl border p-5", up ? "border-emerald-500/25 bg-emerald-500/5" : "border-red-500/25 bg-red-500/5")}>
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-4 w-4 text-primary" />
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Data-Grounded Estimate {data.ai?.ai_generated ? "· AI-explained" : "· model"}
          </span>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className={cn("text-3xl sm:text-4xl font-black font-mono", up ? "text-emerald-500" : "text-red-500")} data-testid={`prediction-band-${data.ticker}`}>
            {band[0]}–{band[1]}%
          </div>
          <div className={cn("flex items-center gap-1 font-semibold mb-1", up ? "text-emerald-500" : "text-red-500")}>
            {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            {up ? "Lean up" : "Lean down"}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          ≈<span data-testid={`prediction-pct-${data.ticker}`} className="font-mono">{pct}%</span> midpoint · a range because markets are uncertain.
          Computed from the metrics + S&P relative strength, over the {data.ai?.prediction_horizon}.
        </p>
      </div>

      {data.benchmark && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid={`benchmark-${data.ticker}`}>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">vs S&P 500 (20 days)</p>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className={cn("text-lg font-bold font-mono", data.benchmark.stock_20d_pct >= 0 ? "text-emerald-500" : "text-red-500")}>
                {data.benchmark.stock_20d_pct >= 0 ? "+" : ""}{data.benchmark.stock_20d_pct}%
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{data.ticker}</div>
            </div>
            <div>
              <div className={cn("text-lg font-bold font-mono", data.benchmark.spy_20d_pct >= 0 ? "text-emerald-500" : "text-red-500")}>
                {data.benchmark.spy_20d_pct >= 0 ? "+" : ""}{data.benchmark.spy_20d_pct}%
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">S&P 500</div>
            </div>
            <div>
              <div className={cn("text-lg font-bold font-mono", data.benchmark.outperforming ? "text-emerald-500" : "text-red-500")}>
                {data.benchmark.relative_strength >= 0 ? "+" : ""}{data.benchmark.relative_strength}%
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Rel. strength</div>
            </div>
          </div>
          <p className={cn("text-xs text-center mt-3 font-medium", data.benchmark.outperforming ? "text-emerald-500" : "text-red-500")}>
            {data.benchmark.outperforming ? "Outperforming the index" : "Lagging the index"}
          </p>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">AI Analysis</p>
        <p className="text-sm leading-relaxed" data-testid={`ai-summary-${data.ticker}`}>{data.ai?.summary}</p>
        <ul className="mt-3 space-y-2">
          {(data.ai?.key_points || []).map((k, i) => (
            <li key={i} className="text-sm flex gap-2 leading-relaxed"><span className="text-primary mt-0.5">•</span>{k}</li>
          ))}
        </ul>
      </div>

      <NewsCard news={data.news} />

      {history.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid={`history-${data.ticker}`}>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" /> Alert History
          </p>
          <ol className="relative border-l border-border ml-1.5 space-y-3">
            {[...history].reverse().map((e, i) => (
              <li key={i} className="ml-4">
                <span className={cn("absolute -left-[5px] mt-1.5 h-2 w-2 rounded-full", e.type === "sentiment" ? "bg-indigo-400" : "bg-primary")} />
                <p className="text-sm leading-snug">{e.message}</p>
                <p className="text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleString()}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Technical Breakdown</p>
        <Bar label="Momentum" value={data.momentum_score} weight={0.4} tint="bg-primary" term="Momentum" />
        <Bar label="MA Trend" value={data.ma_trend_score} weight={0.35} tint="bg-sky-500" term="SMA" />
        <Bar label="RSI (guardrail)" value={data.rsi_score} weight={0.25} tint="bg-indigo-500" term="RSI" />
        <div className="flex items-center gap-1">
          <div className="flex-1"><RiskMeter level={data.risk_level} atrPct={data.atr_pct} /></div>
          <InfoTip term="ATR" className="mt-4" />
        </div>
      </div>
    </div>
  );
}

export default function Search_() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState("single"); // 'single' | 'compare'
  const [qA, setQA] = useState(params.get("q") || "");
  const [qB, setQB] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(() => {
    api.get("/search/recent").then(({ data }) => setRecent(data.recent || [])).catch(() => {});
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const fetchOne = async (symbol) => {
    const { data } = await api.post("/search/scorecard", { ticker: symbol });
    return data;
  };

  const runSingle = useCallback(async (symbol) => {
    const t = (symbol || "").trim().toUpperCase();
    if (!t) return;
    setLoading(true); setError(""); setDataA(null); setDataB(null);
    setParams({ q: t });
    try {
      setDataA(await fetchOne(t));
      loadRecent();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  }, [setParams, loadRecent]);

  const runCompare = async () => {
    const a = qA.trim().toUpperCase(), b = qB.trim().toUpperCase();
    if (!a || !b) { setError("Enter two tickers to compare."); return; }
    setLoading(true); setError(""); setDataA(null); setDataB(null);
    try {
      const [ra, rb] = await Promise.all([fetchOne(a), fetchOne(b)]);
      setDataA(ra); setDataB(rb); loadRecent();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "One of the tickers could not be analyzed.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const initial = params.get("q");
    if (initial) runSingle(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => { e.preventDefault(); mode === "single" ? runSingle(qA) : runCompare(); };

  return (
    <div className={cn("space-y-6", mode === "single" ? "max-w-3xl" : "max-w-5xl")}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" /> AI Stock Scorecard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Search any US ticker for a scored, AI-written educational read — now with a news-sentiment nudge.
          </p>
        </div>
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button data-testid="mode-single" onClick={() => setMode("single")}
            className={cn("px-3 py-1.5 text-sm font-medium", mode === "single" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}>
            Single
          </button>
          <button data-testid="mode-compare" onClick={() => setMode("compare")}
            className={cn("px-3 py-1.5 text-sm font-medium flex items-center gap-1.5", mode === "compare" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}>
            <Columns2 className="h-3.5 w-3.5" /> Compare
          </button>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="search-ticker-input" value={qA} onChange={(e) => setQA(e.target.value)}
            placeholder={mode === "compare" ? "Ticker A, e.g. NVDA" : "Type a ticker, e.g. NVDA"}
            className="pl-9 h-11 font-mono uppercase" maxLength={8} />
        </div>
        {mode === "compare" && (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="search-ticker-input-b" value={qB} onChange={(e) => setQB(e.target.value)}
              placeholder="Ticker B, e.g. AMD" className="pl-9 h-11 font-mono uppercase" maxLength={8} />
          </div>
        )}
        <Button data-testid="search-submit-btn" type="submit" disabled={loading}
          className="h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Analyze
        </Button>
      </form>

      {recent.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="recent-searches">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> Recent
          </span>
          {recent.map((r) => (
            <button key={r.ticker} data-testid={`recent-${r.ticker}`}
              onClick={() => { setMode("single"); setQA(r.ticker); runSingle(r.ticker); }}
              className="text-xs font-mono px-2.5 py-1 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors">
              {r.ticker}
            </button>
          ))}
        </div>
      )}

      {mode === "single" && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground self-center">Try</span>
          {EXAMPLES.map((s) => (
            <button key={s} data-testid={`example-${s}`} onClick={() => { setQA(s); runSingle(s); }}
              className="text-xs font-mono px-2.5 py-1 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div data-testid="search-error" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading && (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
          Fetching market data, headlines & generating the AI scorecard…
        </div>
      )}

      {!loading && (dataA || dataB) && (
        <div data-testid="scorecard-result">
          {mode === "compare" && dataA && dataB ? (
            <div className="grid md:grid-cols-2 gap-5">
              <Scorecard data={dataA} />
              <Scorecard data={dataB} />
            </div>
          ) : dataA ? <Scorecard data={dataA} /> : null}

          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3 mt-5" data-testid="simulation-disclaimer">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-600 dark:text-amber-400/90 leading-relaxed">
              {(dataA || dataB).disclaimer}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
